import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Fade,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import QuizIcon from '@mui/icons-material/Quiz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Course } from '@e-learning/shared/src/store/types';

interface QuizCardProps {
  course: Course;
  index?: number;
  questionCount?: number;
}

const QuizCard: React.FC<QuizCardProps> = ({ course, index = 0, questionCount = 10 }) => {
  const navigate = useNavigate();

  const handleTakeQuiz = () => {
    navigate(`/quizzes/${course.id}`);
  };

  return (
    <Fade in timeout={600 + index * 100}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            borderColor: 'rgba(99, 102, 241, 0.4)',
            boxShadow: '0 24px 48px rgba(99, 102, 241, 0.25)',
          },
        }}
      >
        <Box
          sx={{
            height: 180,
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(236, 72, 153, 0.3) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)',
            },
          }}
        >
          <QuizIcon sx={{ fontSize: 80, color: 'primary.main', position: 'relative', zIndex: 1 }} />
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Chip
              label={course.category}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${questionCount} Questions`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: 'text.secondary',
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '3.5rem',
            }}
          >
            {course.title} Quiz
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flexGrow: 1,
            }}
          >
            Test your knowledge with this comprehensive quiz covering all topics from the {course.title} course.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={handleTakeQuiz}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            Take Quiz
          </Button>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default QuizCard;

